import { StudioSystemType } from 'types/global'
import { ComponentTypes, ComponentTypesV2 } from 'types/selectComponent'

/** Legacy `other` (BOM v3) or V2 other subtypes (e.g. `general`). */
export type StudioComponentLookupType = ComponentTypes | ComponentTypesV2

export type LockableStudioComponent = {
  type?: string
  componentLocked?: boolean
  uuid?: string
}

export const isStudioComponentLocked = (component: LockableStudioComponent): boolean => {
  return (component.type === 'OsBattery' || component.type === 'OsOther') && component.componentLocked === true
}

export const getStudioItemsByCode = (
  componentType: StudioComponentLookupType,
  code: string
): LockableStudioComponent[] => {
  const system: StudioSystemType | undefined = window.editor?.selectedSystem
  if (!system) return []
  switch (componentType) {
    case 'module':
      return system
        .moduleGrids()
        .filter((grid) => grid.moduleType().code === code)
    case 'inverter':
      return system.inverters().filter((inv) => inv.code === code)
    case 'battery':
      return system.batteries().filter((bat) => bat.code === code)
    default:
      return system.others().filter((other) => other.code === code)
  }
}

export const isStudioComponentLockedByCode = (componentType: StudioComponentLookupType, code: string): boolean => {
  return getStudioItemsByCode(componentType, code).some((item) => isStudioComponentLocked(item))
}

export const getStudioItemUuidsByCode = (componentType: StudioComponentLookupType, code: string): string[] => {
  return getStudioItemsByCode(componentType, code)
    .map((item) => item.uuid)
    .filter((uuid): uuid is string => Boolean(uuid))
}

export const STUDIO_COMPONENT_BATTERY_LOCKED_MESSAGE =
  'Component is locked because this battery is part of an all in one system'

export const STUDIO_COMPONENT_SOLVERA_LOCKED_MESSAGE =
  "This component can't be removed because this design is eligible for the Solvera Sunshine Guarantee."

export const getStudioComponentLockedMessage = (component: LockableStudioComponent): string => {
  if (component.type === 'OsBattery') {
    return STUDIO_COMPONENT_BATTERY_LOCKED_MESSAGE
  }
  return STUDIO_COMPONENT_SOLVERA_LOCKED_MESSAGE
}

export const showStudioDesignWarning = (message: string): void => {
  window.Designer?.showNotification?.(message, 'warning')
}

export const getFirstLockedStudioTarget = (targetUuids?: string[]): LockableStudioComponent | undefined => {
  if (!targetUuids?.length) return undefined
  for (const uuid of targetUuids) {
    const obj = window.editor?.objectByUuid?.(uuid) as LockableStudioComponent | undefined
    if (obj && isStudioComponentLocked(obj)) {
      return obj
    }
  }
  return undefined
}

export const shouldBlockLockedStudioTargetReplacement = ({
  targetUuids,
  autoAdded,
  authoredBy,
}: {
  targetUuids?: string[]
  autoAdded?: boolean
  authoredBy?: string
}): boolean => {
  if (autoAdded || authoredBy) return false
  return getFirstLockedStudioTarget(targetUuids) !== undefined
}
